/**
 * What every component shares: an open shadow root, the common stylesheet,
 * and render batching.
 *
 * Setting several properties in a row - the table hands a seat its player,
 * color and turn flag one after another - queues a single render on the
 * microtask queue rather than one per property.
 */

import baseStyles from "./base.css";

export abstract class BaseElement extends HTMLElement {
    protected readonly root: ShadowRoot;

    #queued = false;

    protected constructor(...sheets: readonly CSSStyleSheet[]) {
        super();

        this.root = this.attachShadow({ mode: "open" });
        this.root.adoptedStyleSheets = [baseStyles, ...sheets];
    }

    connectedCallback(): void {
        this.render();
    }

    /** Queues a render; any number of calls before it runs collapse into one. */
    protected invalidate(): void {
        if (this.#queued) {
            return;
        }

        this.#queued = true;

        queueMicrotask(() => {
            this.#queued = false;

            if (this.isConnected) {
                this.render();
            }
        });
    }

    protected abstract render(): void;
}
